import { type Cidr, parseCidr } from '@it-tools/core-net'
import type { CloudMode } from '@it-tools/core-net'
import { computed, ref, shallowRef } from 'vue'
import {
  changeRoot,
  createDesign,
  type Design,
  joinSubnet,
  setNote,
  splitSubnet,
  type SubnetNote,
} from './design'

export const DEFAULT_NETWORK = '10.0.0.0/16'
const HISTORY_LIMIT = 100

/**
 * The calculator's single source of truth, with undo/redo.
 *
 * Every mutation replaces the whole immutable `Design`, so history is just a stack
 * of past values — cheap, because leaves are shared structure.
 */
export function useDesign(initial: string = DEFAULT_NETWORK) {
  const design = shallowRef<Design>(createDesign(parseCidr(initial)))
  const cloudMode = ref<CloudMode>('standard')
  const past = shallowRef<Design[]>([])
  const future = shallowRef<Design[]>([])

  function commit(next: Design): void {
    if (next === design.value) return
    past.value = [...past.value, design.value].slice(-HISTORY_LIMIT)
    future.value = []
    design.value = next
  }

  return {
    design,
    cloudMode,
    root: computed(() => design.value.tree.root),
    leaves: computed(() => design.value.tree.leaves),
    canUndo: computed(() => past.value.length > 0),
    canRedo: computed(() => future.value.length > 0),

    split: (target: Cidr) => commit(splitSubnet(design.value, target)),
    // The bracket hands us an ancestor BLOCK, not a leaf — joining collapses it.
    join: (block: Cidr) => commit(joinSubnet(design.value, block)),
    annotate: (target: Cidr, patch: Partial<SubnetNote>) =>
      commit(setNote(design.value, target, patch)),
    setRoot: (root: Cidr) => commit(changeRoot(design.value, root)),
    reset: () => commit(createDesign(design.value.tree.root)),

    /** Replaces the whole design, e.g. when restoring a shared link. */
    load(next: Design, mode?: CloudMode): void {
      commit(next)
      if (mode) cloudMode.value = mode
    },

    undo(): void {
      const previous = past.value.at(-1)
      if (!previous) return
      past.value = past.value.slice(0, -1)
      future.value = [design.value, ...future.value]
      design.value = previous
    },
    redo(): void {
      const next = future.value[0]
      if (!next) return
      future.value = future.value.slice(1)
      past.value = [...past.value, design.value]
      design.value = next
    },
  }
}
